import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import WorkCard from '@/components/base/WorkCard';
import FilterControls, { type FilterState } from '@/pages/category/components/FilterControls';
import { typeMeta } from '@/mocks/movies';
import { filterWorks } from '@/utils/content';

const PAGE_SIZE = 8;

const defaultFilter: FilterState = {
  genre: '全部',
  year: '全部',
  region: '全部',
  sort: 'latest',
};

export default function CategoryPage() {
  const { type = 'movie' } = useParams();
  const meta = typeMeta[type];
  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilter(defaultFilter);
    setPage(1);
  }, [type]);

  useEffect(() => {
    if (meta) {
      document.title = `${meta.title} - 长日光阴影视推荐平台`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', `${meta.title}：${meta.description}`);
    }
  }, [meta]);

  const results = useMemo(
    () => (meta ? filterWorks({ type, ...filter }) : []),
    [type, filter, meta],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const resetFilter = () => {
    setFilter(defaultFilter);
    setPage(1);
  };

  if (!meta) {
    return (
      <div className="mx-auto w-full px-4 py-20 md:px-6">
        <EmptyState
          title="暂无内容，敬请期待"
          description="该分类暂未开放，请从导航栏选择电影、电视剧或综艺。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/');
          }}
        />
      </div>
    );
  }

  const filterPanel = <FilterControls type={type} value={filter} onChange={handleFilterChange} />;

  return (
    <>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img src={meta.banner} alt={`${meta.title} 横幅`} className="h-44 w-full object-top opacity-60 md:h-56" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 to-primary-950/40" />
        <div className="absolute inset-0 mx-auto flex w-full flex-col justify-center px-4 md:px-6">
          <h1 className="font-heading text-2xl font-black text-background-50 md:text-4xl">{meta.title}</h1>
          <p className="mt-2 max-w-2xl text-xs leading-relaxed text-background-200 md:text-sm">{meta.description}</p>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '分类', to: `/category/${type}` }, { label: meta.label }]} />
      </div>

      <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 pb-16 md:px-6 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={resetFilter}
                className="whitespace-nowrap text-xs font-medium text-accent-600 transition-colors hover:text-accent-700"
              >
                重置筛选
              </button>
            </div>
            {filterPanel}
          </div>
        </aside>

        <div>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-bold text-primary-600">{results.length}</span> 部作品
            </p>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 lg:hidden"
            >
              <i className="ri-equalizer-line" aria-hidden="true" />
              筛选
            </button>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {pageItems.map((work) => (
                  <WorkCard key={work.id} work={work} showSynopsis />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage(currentPage - 1)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setPage(index + 1)}
                      className={`h-9 min-w-9 rounded-md px-3 text-sm font-medium transition-colors ${
                        currentPage === index + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              title="暂无符合条件的内容"
              description="换个筛选条件试试，或许会有意想不到的发现。"
              actionLabel="重置筛选"
              onAction={resetFilter}
            />
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-primary-950/50" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 right-0 flex w-[85%] max-w-sm flex-col bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-800"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">{filterPanel}</div>
            <div className="flex gap-3 border-t border-background-200 px-5 py-4">
              <button
                type="button"
                onClick={resetFilter}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800"
              >
                重置筛选
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}